import {
  Autocomplete,
  AutocompleteProps,
  Chip,
  Stack,
  TextField,
  TextFieldVariants,
  Typography,
} from '@mui/material';
import {
  Controller,
  ControllerRenderProps,
  FieldError,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Option {
  value: string;
  label: string;
}

interface Props<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends AutocompleteProps<Option, Multiple, DisableClearable, FreeSolo> {
  name: string;
  label?: string;
  title?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  noHelperText?: boolean;
  canAddNew?: boolean;
  variant?: TextFieldVariants
}

export default function RHFAutocomplete<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  name,
  label,
  title,
  helperText,
  placeholder,
  canAddNew = false,
  noHelperText = false,
  variant = 'outlined',
  ...other
}: Omit<Props<Multiple, DisableClearable, FreeSolo>, 'renderInput'>) {
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();

  const placeholderOrTitle = placeholder ?? title;

  function renderField(field: ControllerRenderProps<FieldValues, string>, error?: FieldError) {
    const textField = (
      <Autocomplete
        {...field}
        blurOnSelect
        disableCloseOnSelect
        onChange={(_, newValue) => {
          if (newValue && typeof newValue === 'object') {
            setValue(name, newValue, { shouldValidate: true });
          }
        }}
        isOptionEqualToValue={(option, value) =>
          value.value === '' ||
          JSON.stringify(value) === JSON.stringify({ value: '', label: '' }) ||
          option === value ||
          JSON.stringify(option) === JSON.stringify(value) ||
          (typeof value === 'object' &&
            value !== null &&
            JSON.stringify(value) === JSON.stringify({ label: '', value: '' }))
        }
        renderInput={(params) => (
          <TextField
            label={label ? t(label) : undefined}
            placeholder={placeholderOrTitle ? t(placeholderOrTitle) : undefined}
            error={!!error}
            helperText={!noHelperText && error ? error?.message : helperText}
            variant={variant}
            {...params}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {t(option.label)}
          </li>
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              label={t(option.label)}
              size="small"
              color="primary"
              variant="soft"
            />
          ))
        }
        {...other}
      />
    );

    if (!title) return <>{textField}</>;

    return (
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">{t(title)}</Typography>
        {textField}
      </Stack>
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => renderField(field, error)}
    />
  );
}
